export interface RoleColorSet {
  accentColor: string; // left border + pill stroke
  avatarBg: string; // avatar circle background
  avatarText: string; // initials text color
  pillBg: string; // role pill background
  pillText: string; // role pill text color
}

// Keys are member_type_name values as returned by the API, lowercased.
// Match is done with .toLowerCase().includes() or exact match.
export const ROLE_COLOR_MAP: Record<string, RoleColorSet> = {
  'head of household': {
    accentColor: 'primary.main',
    avatarBg: 'primary.lighter',
    avatarText: 'primary.dark',
    pillBg: 'primary.lighter',
    pillText: 'primary.dark',
  },
  'co-head': {
    accentColor: 'info.main',
    avatarBg: 'info.lighter',
    avatarText: 'info.dark',
    pillBg: 'info.lighter',
    pillText: 'info.dark',
  },
  spouse: {
    accentColor: 'info.main',
    avatarBg: 'info.lighter',
    avatarText: 'info.dark',
    pillBg: 'info.lighter',
    pillText: 'info.dark',
  },
  'other adult': {
    accentColor: 'warning.main',
    avatarBg: 'warning.lighter',
    avatarText: 'warning.dark',
    pillBg: 'warning.lighter',
    pillText: 'warning.dark',
  },
  'full-time student': {
    accentColor: '#7635DC',
    avatarBg: '#EDE9FE',
    avatarText: '#430EA6',
    pillBg: '#EDE9FE',
    pillText: '#430EA6',
  },
  dependent: {
    accentColor: '#EC4899',
    avatarBg: '#FDF2F8',
    avatarText: '#9D174D',
    pillBg: '#FDF2F8',
    pillText: '#9D174D',
  },
};

export const DEFAULT_ROLE_COLORS: RoleColorSet = {
  accentColor: 'grey.500',
  avatarBg: 'action.hover',
  avatarText: 'text.secondary',
  pillBg: 'action.hover',
  pillText: 'text.secondary',
};

// Lookup helper — partial match (covers "Co-Head of Household", "Full-Time Student 18+", etc.)
// Entries are sorted by key length ascending so shorter, more-specific keys like
// "co-head" are checked before "head of household", preventing substring collisions
// (e.g. "Co-Head of Household" contains "head of household" as a substring).
export function getRoleColors(
  memberTypeName: string | null | undefined
): RoleColorSet {
  if (!memberTypeName) {
    return DEFAULT_ROLE_COLORS;
  }
  const lower = memberTypeName.toLowerCase();
  const sortedEntries = Object.entries(ROLE_COLOR_MAP).sort(
    (a, b) => a[0].length - b[0].length
  );
  for (const [key, colors] of sortedEntries) {
    if (lower.includes(key)) {
      return colors;
    }
  }
  return DEFAULT_ROLE_COLORS;
}

/**
 * Returns true for member types that represent minors or dependants.
 * These members should not have contact information collected or displayed.
 * Matches "Dependent", "Dependent (Under 18)", "Minor", etc. case-insensitively.
 */
export function isMinorMemberType(
  memberTypeName: string | null | undefined
): boolean {
  if (!memberTypeName) {
    return false;
  }
  const lower = memberTypeName.toLowerCase();
  return (
    lower.includes('dependent') ||
    lower.includes('minor') ||
    lower.includes('under 18') ||
    lower.includes('17+')
  );
}
